import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../../config';
import { Download, Trash2, Loader2, RefreshCw, Copy, Check, Music } from 'lucide-react';

const STATUS_BADGE = {
  queued: { text: 'In Warteschlange', cls: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
  processing: { text: 'Wird analysiert', cls: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  completed: { text: 'Fertig', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  failed: { text: 'Fehler', cls: 'bg-red-500/20 text-red-300 border-red-500/30' },
};

function fmtDate(iso) {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function fmtDuration(s) {
  if (s == null) return '–';
  const mins = Math.floor(s / 60);
  const secs = Math.round(s % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

function PromptCard({ title, tags, description, compact = false }) {
  const [copied, setCopied] = useState(null);
  const copy = (text, which) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied(null), 1200);
  };
  return (
    <div className={`bg-black/40 border border-white/10 rounded-lg ${compact ? 'p-3' : 'p-4'} space-y-2`}>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-emerald-300">{title}</h4>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs uppercase tracking-wide text-white/50">Style Tags</span>
          <button
            onClick={() => copy(tags, 'tags')}
            className="text-xs text-white/60 hover:text-white flex items-center gap-1"
          >
            {copied === 'tags' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            Kopieren
          </button>
        </div>
        <div className="bg-black/60 border border-white/10 rounded p-2 text-xs text-white/90 font-mono break-words">
          {tags || '–'}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs uppercase tracking-wide text-white/50">Description</span>
          <button
            onClick={() => copy(description, 'desc')}
            className="text-xs text-white/60 hover:text-white flex items-center gap-1"
          >
            {copied === 'desc' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            Kopieren
          </button>
        </div>
        <div className="bg-black/60 border border-white/10 rounded p-2 text-xs text-white/90 whitespace-pre-wrap break-words">
          {description || '–'}
        </div>
      </div>
    </div>
  );
}

function formatTimestamp(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const mins = Math.floor(s / 60);
  const secs = s - mins * 60;
  return `[${String(mins).padStart(2, '0')}:${secs.toFixed(1).padStart(4, '0')}]`;
}

function LyricsPanel({ job }) {
  const lines = Array.isArray(job.lyrics_lines) ? job.lyrics_lines : [];
  const structure = Array.isArray(job.lyrics_structure) ? job.lyrics_structure : [];
  const language = job.lyrics_language;
  const isInstrumental = Boolean(job.is_instrumental);

  // Zeilen in Struktur-Abschnitte einsortieren
  const sectionsWithLines = structure.map((seg) => {
    const start = Number(seg.start) || 0;
    const end = Number(seg.end) || 0;
    const bucket = lines.filter((ln) => {
      const t = Number(ln.start) || 0;
      return t >= start && t < end;
    });
    return { ...seg, lines: bucket };
  });
  const leftovers = structure.length
    ? lines.filter((ln) => {
        const t = Number(ln.start) || 0;
        return !structure.some((s) => t >= (Number(s.start) || 0) && t < (Number(s.end) || 0));
      })
    : [];

  return (
    <div className="mt-4 pt-4 border-t border-white/10 space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <h4 className="text-sm font-semibold text-amber-200">Songtext & Struktur</h4>
        {language && (
          <span className="px-2 py-0.5 rounded text-[11px] bg-white/10 text-white/70 border border-white/10">
            Sprache: {language}
          </span>
        )}
        {isInstrumental && (
          <span className="px-2 py-0.5 rounded text-[11px] bg-white/10 text-white/70 border border-white/10">
            Instrumental
          </span>
        )}
      </div>

      {isInstrumental && !lines.length && (
        <div className="text-xs text-white/60">Kein Gesang erkannt — Instrumental-Track.</div>
      )}

      {structure.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-white/50 mb-2">Struktur</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-white/50">
                  <th className="text-left py-1 pr-3 font-medium">Abschnitt</th>
                  <th className="text-left py-1 pr-3 font-medium">Start</th>
                  <th className="text-left py-1 pr-3 font-medium">Ende</th>
                  <th className="text-left py-1 font-medium">Dauer</th>
                </tr>
              </thead>
              <tbody>
                {structure.map((seg, idx) => {
                  const start = Number(seg.start) || 0;
                  const end = Number(seg.end) || 0;
                  return (
                    <tr key={idx} className="border-t border-white/5">
                      <td className="py-1 pr-3 text-white/90">
                        {seg.label}
                        {seg.is_hook && <span className="ml-1 text-amber-300">★</span>}
                      </td>
                      <td className="py-1 pr-3 text-white/70 font-mono">{formatTimestamp(start)}</td>
                      <td className="py-1 pr-3 text-white/70 font-mono">{formatTimestamp(end)}</td>
                      <td className="py-1 text-white/70 font-mono">{Math.max(0, end - start).toFixed(1)}s</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {lines.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-white/50 mb-2">Lyrics</div>
          <div className="bg-black/60 border border-white/10 rounded p-3 space-y-3 max-h-[480px] overflow-y-auto">
            {structure.length > 0 ? (
              <>
                {sectionsWithLines
                  .filter((seg) => seg.lines.length > 0)
                  .map((seg, idx) => (
                    <div key={idx}>
                      <div className="text-[11px] uppercase tracking-wider text-amber-200 mb-1">
                        {seg.label}
                        {seg.is_hook && <span className="ml-1">★</span>}
                      </div>
                      <div className="space-y-0.5 font-mono text-xs text-white/90">
                        {seg.lines.map((ln, j) => (
                          <div key={j}>
                            <span className="text-white/40 mr-2">{formatTimestamp(ln.start)}</span>
                            {ln.text}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                {leftovers.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-white/50 mb-1">Weitere Zeilen</div>
                    <div className="space-y-0.5 font-mono text-xs text-white/80">
                      {leftovers.map((ln, j) => (
                        <div key={j}>
                          <span className="text-white/40 mr-2">{formatTimestamp(ln.start)}</span>
                          {ln.text}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-0.5 font-mono text-xs text-white/90">
                {lines.map((ln, idx) => (
                  <div key={idx}>
                    <span className="text-white/40 mr-2">{formatTimestamp(ln.start)}</span>
                    {ln.text}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MusicAnalyzerAdmin({ adminToken }) {
  const [jobs, setJobs] = useState([]);
  const [sourceMode, setSourceMode] = useState('url'); // 'url' | 'upload'
  const [url, setUrl] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [expandedLyrics, setExpandedLyrics] = useState(null);
  const pollRef = useRef(null);

  const authHeaders = useCallback(
    (extra = {}) => ({ Authorization: `Bearer ${adminToken}`, ...extra }),
    [adminToken]
  );

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const loadJobs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/music-analyzer/jobs`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setJobs(data.jobs || []);
      }
    } catch (err) {
      console.error('Music-Jobs laden fehlgeschlagen', err);
    }
  }, [authHeaders]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  useEffect(() => {
    const hasActive = jobs.some((j) => j.status === 'queued' || j.status === 'processing');
    if (hasActive) {
      pollRef.current = setInterval(loadJobs, 4000);
      return () => clearInterval(pollRef.current);
    }
  }, [jobs, loadJobs]);

  const submitJob = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let res;
      if (sourceMode === 'upload') {
        if (!uploadFile) {
          showMessage('error', 'Bitte eine Audio-/Videodatei auswählen');
          setSubmitting(false);
          return;
        }
        res = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${API_URL}/api/admin/music-analyzer/jobs/upload`);
          xhr.setRequestHeader('Authorization', `Bearer ${adminToken}`);
          xhr.upload.onprogress = (ev) => {
            if (ev.lengthComputable) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
          };
          xhr.onload = () =>
            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              json: () => Promise.resolve(JSON.parse(xhr.responseText || '{}')),
            });
          xhr.onerror = () => reject(new Error('Netzwerk-Fehler'));
          const fd = new FormData();
          fd.append('file', uploadFile);
          xhr.send(fd);
        });
      } else {
        if (!url.trim()) {
          showMessage('error', 'Bitte eine URL eingeben');
          setSubmitting(false);
          return;
        }
        res = await fetch(`${API_URL}/api/admin/music-analyzer/jobs`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ url: url.trim() }),
        });
      }

      if (res.ok) {
        const newJob = await res.json();
        setJobs((prev) => [newJob, ...prev]);
        setUrl('');
        setUploadFile(null);
        setUploadProgress(0);
        showMessage('success', 'Analyse gestartet – läuft im Hintergrund');
      } else {
        const err = await res.json().catch(() => ({}));
        showMessage('error', err.detail || 'Analyse konnte nicht gestartet werden');
      }
    } catch (err) {
      showMessage('error', `Netzwerk-Fehler: ${err.message}`);
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Analyse und Report löschen?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/music-analyzer/jobs/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) setJobs((prev) => prev.filter((j) => j.id !== id));
    } catch (err) {
      showMessage('error', `Netzwerk-Fehler: ${err.message}`);
    }
  };

  const downloadReport = async (job) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/music-analyzer/jobs/${job.id}/download`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        showMessage('error', 'Download fehlgeschlagen');
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeTitle = (job.title || 'musikanalyse').replace(/[^\w\s.-]/g, '').replace(/\s+/g, '-').slice(0, 80);
      a.href = blobUrl;
      a.download = `${safeTitle}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      showMessage('error', `Netzwerk-Fehler: ${err.message}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <Music className="w-8 h-8 text-emerald-400 flex-shrink-0 mt-1" />
        <div>
          <h2 className="text-2xl font-bold text-white">Musik- & Song-Analyse</h2>
          <p className="text-white/60 text-sm mt-1">
            URL (YouTube, SoundCloud, Bandcamp, Direktlink …) oder Audio-/Videodatei hochladen → KI erzeugt eine komplette Musikanalyse inkl. fertiger Suno-Prompts (3 Varianten).
          </p>
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded-xl border ${
          message.type === 'success'
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
            : 'bg-red-500/10 border-red-500/30 text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={submitJob} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <h3 className="text-lg font-semibold text-white">Neue Analyse starten</h3>

        <div className="flex gap-2 p-1 bg-black/40 rounded-lg w-fit">
          <button
            type="button"
            onClick={() => setSourceMode('url')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              sourceMode === 'url' ? 'bg-emerald-500 text-black' : 'text-white/70 hover:text-white'
            }`}
          >
            🔗 URL
          </button>
          <button
            type="button"
            onClick={() => setSourceMode('upload')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              sourceMode === 'upload' ? 'bg-emerald-500 text-black' : 'text-white/70 hover:text-white'
            }`}
          >
            📁 Datei hochladen
          </button>
        </div>

        {sourceMode === 'url' ? (
          <div>
            <label className="block text-sm text-white/70 mb-1">Song- oder Musikvideo-URL</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://… (YouTube, SoundCloud, Bandcamp, .mp3-Direktlink …)"
              className="w-full bg-black/60 border border-white/20 rounded-lg p-3 text-white"
            />
            <p className="mt-1 text-xs text-white/40">Spotify-Links funktionieren nicht – bitte YouTube/SoundCloud des gleichen Songs nutzen.</p>
          </div>
        ) : (
          <div>
            <label className="block text-sm text-white/70 mb-1">Audio/Video-Datei (mp3, wav, flac, m4a, mp4, … – max. 300 MB)</label>
            <input
              type="file"
              accept=".mp3,.wav,.flac,.ogg,.m4a,.aac,.opus,.mp4,.webm,.mkv,.mov,.avi,audio/*,video/*"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="w-full bg-black/60 border border-white/20 rounded-lg p-3 text-white file:mr-3 file:px-3 file:py-1 file:rounded file:border-0 file:bg-white/10 file:text-white"
            />
            {uploadFile && (
              <p className="mt-2 text-xs text-white/60">
                {uploadFile.name} ({(uploadFile.size / (1024 * 1024)).toFixed(1)} MB)
              </p>
            )}
            {submitting && uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-2">
                <div className="w-full bg-black/60 rounded h-2 overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
                <p className="mt-1 text-xs text-white/60">Upload: {uploadProgress}%</p>
              </div>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || (sourceMode === 'url' ? !url.trim() : !uploadFile)}
          className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold rounded-lg flex items-center gap-2"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : '🎵'}
          Analyse starten
        </button>
      </form>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Analysen ({jobs.length})</h3>
          <button onClick={loadJobs} className="text-white/60 hover:text-white p-2" title="Aktualisieren">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {jobs.length === 0 ? (
          <p className="text-white/50 text-center py-8">Noch keine Analysen.</p>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => {
              const badge = STATUS_BADGE[job.status] || STATUS_BADGE.queued;
              const open = expanded === job.id;
              return (
                <div key={job.id} className="bg-black/40 border border-white/10 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${badge.cls}`}>{badge.text}</span>
                        <span className="px-2 py-0.5 rounded text-xs bg-white/10 text-white/70 border border-white/10">
                          {job.source_kind === 'upload' ? '📁 Upload' : '🔗 URL'}
                        </span>
                        <span className="text-white font-medium truncate">{job.title || job.original_filename || job.source}</span>
                      </div>
                      <div className="text-xs text-white/50 mt-1 break-all">
                        {job.source_kind === 'upload' ? job.original_filename : job.source}
                      </div>
                      <div className="flex gap-3 text-xs text-white/50 mt-2 flex-wrap">
                        <span>Erstellt: {fmtDate(job.created_at)}</span>
                        {job.duration_seconds != null && <span>Dauer: {fmtDuration(job.duration_seconds)}</span>}
                        {job.bpm != null && <span className="text-emerald-300">BPM: {Math.round(job.bpm)}</span>}
                        {job.key && <span className="text-emerald-300">Tonart: {job.key} {job.scale}</span>}
                        {job.genre_main && <span className="text-emerald-300">{job.genre_main}{job.genre_sub ? ` / ${job.genre_sub}` : ''}</span>}
                      </div>
                      {job.status === 'failed' && job.error && (
                        <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-300">
                          {job.error}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {job.status === 'completed' && (
                        <>
                          <button
                            onClick={() => setExpanded(open ? null : job.id)}
                            className="px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 rounded-lg text-sm font-medium"
                          >
                            {open ? 'Einklappen' : 'Suno-Prompts'}
                          </button>
                          {(job.is_instrumental || (job.lyrics_lines && job.lyrics_lines.length) || (job.lyrics_structure && job.lyrics_structure.length)) && (
                            <button
                              onClick={() => setExpandedLyrics(expandedLyrics === job.id ? null : job.id)}
                              className="px-3 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 rounded-lg text-sm font-medium"
                            >
                              {expandedLyrics === job.id ? 'Einklappen' : 'Songtext'}
                            </button>
                          )}
                          <button
                            onClick={() => downloadReport(job)}
                            className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg"
                            title="Markdown-Report herunterladen"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => deleteJob(job.id)}
                        className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg"
                        title="Löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {open && job.status === 'completed' && job.suno_prompts && (
                    <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                      <PromptCard title="Minimal" tags={job.suno_prompts.minimal?.style_tags} description={job.suno_prompts.minimal?.description} />
                      <PromptCard title="Standard" tags={job.suno_prompts.standard?.style_tags} description={job.suno_prompts.standard?.description} />
                      <PromptCard title="Detailliert" tags={job.suno_prompts.detailed?.style_tags} description={job.suno_prompts.detailed?.description} />
                      {job.suno_parameters && (
                        <div className="text-xs text-white/60 flex gap-4 flex-wrap">
                          <span>Empfohlen BPM: <span className="text-white">{job.suno_parameters.suggested_bpm || '–'}</span></span>
                          <span>Key: <span className="text-white">{job.suno_parameters.suggested_key || '–'}</span></span>
                          <span>Instrumental: <span className="text-white">{job.suno_parameters.instrumental_only ? 'ja' : 'nein'}</span></span>
                        </div>
                      )}
                      {job.production_notes && (
                        <div>
                          <div className="text-xs uppercase tracking-wide text-white/50 mb-1">Produktionshinweise</div>
                          <div className="bg-black/60 border border-white/10 rounded p-2 text-xs text-white/80 whitespace-pre-wrap">
                            {job.production_notes}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {expandedLyrics === job.id && job.status === 'completed' && (
                    <LyricsPanel job={job} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
